/**
 * Merge a cross-audit slice Firestore document into the multi-chart nursing form shape.
 * Shared by submission hydration and realtime sidebar sync (same row semantics as SidePanel).
 */
import {
  extractPrimaryAnswerRowFromComplianceDoc,
  normalizeSubmissionShape,
} from '../services/submissionShape';
import type { SubmissionDocument } from '../services/submissions';

/**
 * Copy ตรวจไขว้ row fields from slice docs into the multi-chart nursing payload (parent nurse_notes omits these).
 * Uses parallel arrays by chart index — same shape as SidePanel state (`answers[chartIndex]`, `focus[chartIndex]`, …).
 */
export const mergeCrossAuditChartRowFromDoc = (
  nursingData: SubmissionDocument,
  chartIndex: number,
  crossDoc: SubmissionDocument,
): void => {
  const slice = normalizeSubmissionShape(crossDoc as Record<string, unknown>);
  const target = nursingData as Record<string, unknown>;
  const i = chartIndex;

  const getFirstNonEmptyRow = (arr: unknown[]): unknown[] | undefined => {
    const found = arr.find(
      (row) => Array.isArray(row) && row.some((c) => String(c ?? '').trim() !== ''),
    );
    return found as unknown[] | undefined;
  };
  const getFirstNonEmptyString = (arr: unknown[]): string | undefined => {
    const found = arr.find((item) => String(item ?? '').trim() !== '');
    return found !== undefined ? String(found) : undefined;
  };

  const primaryRow = extractPrimaryAnswerRowFromComplianceDoc(crossDoc as Record<string, unknown>);
  let activeAnswerRow: unknown[] | undefined =
    primaryRow && primaryRow.some((c) => String(c ?? '').trim() !== '') ? primaryRow : undefined;
  if (!activeAnswerRow && slice.answers.length > 0) {
    activeAnswerRow = getFirstNonEmptyRow(slice.answers as unknown[]);
  }

  if (activeAnswerRow && Array.isArray(activeAnswerRow)) {
    const prev = Array.isArray(target.answers) ? [...(target.answers as unknown[][])] : [];
    while (prev.length <= i) prev.push([]);
    prev[i] = [...activeAnswerRow];
    target.answers = prev;
  }

  const activeFocus = getFirstNonEmptyString(slice.focus as unknown[]);
  if (activeFocus !== undefined) {
    const prev = Array.isArray(target.focus) ? [...(target.focus as string[])] : [];
    while (prev.length <= i) prev.push('');
    prev[i] = activeFocus;
    target.focus = prev;
  }

  const activeTreatments = getFirstNonEmptyString(slice.treatments as unknown[]);
  if (activeTreatments !== undefined) {
    const prev = Array.isArray(target.treatments) ? [...(target.treatments as string[])] : [];
    while (prev.length <= i) prev.push('');
    prev[i] = activeTreatments;
    target.treatments = prev;
  }

  const fd = crossDoc as Record<string, unknown>;
  const mergeParallelString = (key: 'disease' | 'dates' | 'shifts' | 'hn') => {
    const v = fd[key];
    if (!Array.isArray(v)) return;
    const activeVal = getFirstNonEmptyString(v);
    if (activeVal !== undefined) {
      const cur = Array.isArray(target[key]) ? [...(target[key] as string[])] : [];
      while (cur.length <= i) cur.push('');
      cur[i] = activeVal;
      target[key] = cur;
    }
  };

  mergeParallelString('disease');
  mergeParallelString('dates');
  mergeParallelString('shifts');
  mergeParallelString('hn');

  if (Array.isArray(fd.suggestions)) {
    const activeSug = getFirstNonEmptyString(fd.suggestions);
    if (activeSug !== undefined) {
      const cur = Array.isArray(target.suggestions) ? [...(target.suggestions as string[])] : [];
      while (cur.length <= i) cur.push('');
      cur[i] = activeSug;
      target.suggestions = cur;
    }
  }

  if (Array.isArray(fd.problems)) {
    const activeProb = getFirstNonEmptyRow(fd.problems as unknown[]);
    if (activeProb) {
      const cur = Array.isArray(target.problems)
        ? [...(target.problems as unknown[][])].map((r) => (Array.isArray(r) ? [...r] : []))
        : [];
      while (cur.length <= i) cur.push([]);
      cur[i] = activeProb.map((c) => String(c ?? ''));
      target.problems = cur;
    }
  }

  if (Array.isArray(fd.criteria)) {
    const activeCrit = getFirstNonEmptyString(fd.criteria);
    if (activeCrit !== undefined) {
      const cur = Array.isArray(target.criteria) ? [...(target.criteria as string[])] : [];
      while (cur.length <= i) cur.push('');
      cur[i] = activeCrit;
      target.criteria = cur;
    }
  }
};
